import { HTMLAttributes, MouseEvent, WheelEvent, useCallback, useEffect, useRef } from "react"
import classNames from "classnames"
import { getGlobalMapRef, getViewport, viewStateEventEmitter } from "./GlobalViewState"

const forwardToMap = (event: Event) => {
    getGlobalMapRef()?.current?.getMap()?.getCanvas()?.dispatchEvent(event)
}

const forwardWheelToMap = (event: WheelEvent<HTMLDivElement>) =>
    forwardToMap(
        new window.WheelEvent("wheel", {
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaMode: event.deltaMode,
            clientX: event.clientX,
            clientY: event.clientY,
            bubbles: true,
            cancelable: true,
        })
    )

const forwardMouseMoveToMap = (event: MouseEvent<HTMLDivElement>) =>
    forwardToMap(
        new window.MouseEvent("mousemove", {
            clientX: event.clientX,
            clientY: event.clientY,
            bubbles: true,
            cancelable: true,
        })
    )

export type NovaMarkerAnchor =
    | "center"
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"

const anchorTranslate: Record<NovaMarkerAnchor, string> = {
    center: "translate(-50%, -50%)",
    top: "translate(-50%, 0)",
    bottom: "translate(-50%, -100%)",
    left: "translate(0, -50%)",
    right: "translate(-100%, -50%)",
    "top-left": "translate(0, 0)",
    "top-right": "translate(-100%, 0)",
    "bottom-left": "translate(0, -100%)",
    "bottom-right": "translate(-100%, -100%)",
}

interface NovaMarkerProps extends Omit<HTMLAttributes<HTMLDivElement>, "onDragEnd"> {
    lat: number
    lng: number
    anchor?: NovaMarkerAnchor
    scrollThroughToMap?: boolean
    draggable?: boolean
    onDragEnd?: (position: { lat: number; lng: number }) => void
}

export const NovaMarker = ({
    lat,
    lng,
    anchor = "top-left",
    scrollThroughToMap = true,
    draggable = false,
    onDragEnd,
    className,
    children,
    ...rest
}: NovaMarkerProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const dragPositionRef = useRef({ x: 0, y: 0 })
    const grabOffsetRef = useRef({ x: 0, y: 0 })
    const isDraggingRef = useRef(false)

    const applyTransform = useCallback(
        (x: number, y: number) => {
            if (containerRef.current) {
                containerRef.current.style.transform = `translate(${x}px, ${y}px) ${anchorTranslate[anchor]}`
            }
        },
        [anchor]
    )

    useEffect(() => {
        const applyPosition = () => {
            if (isDraggingRef.current) return
            const [x, y] = getViewport().project([lng, lat])
            dragPositionRef.current = { x, y }
            applyTransform(x, y)
        }
        applyPosition()
        viewStateEventEmitter.on("updatedViewport", applyPosition)
        return () => {
            viewStateEventEmitter.off("updatedViewport", applyPosition)
        }
    }, [lat, lng, anchor, applyTransform])

    const pointerToParent = (event: React.PointerEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.offsetParent?.getBoundingClientRect()
        return {
            x: rect ? event.clientX - rect.left : event.clientX,
            y: rect ? event.clientY - rect.top : event.clientY,
        }
    }

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!draggable) return
        event.stopPropagation()
        const pointer = pointerToParent(event)
        grabOffsetRef.current = {
            x: pointer.x - dragPositionRef.current.x,
            y: pointer.y - dragPositionRef.current.y,
        }
        isDraggingRef.current = true
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!draggable || !isDraggingRef.current) return
        event.stopPropagation()
        const pointer = pointerToParent(event)
        const x = pointer.x - grabOffsetRef.current.x
        const y = pointer.y - grabOffsetRef.current.y
        dragPositionRef.current = { x, y }
        applyTransform(x, y)
    }

    const releasePointer = (event: React.PointerEvent<HTMLDivElement>) => {
        isDraggingRef.current = false
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
        }
    }

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggingRef.current) return
        event.stopPropagation()
        try {
            const [nextLng, nextLat] = getViewport().unproject([
                dragPositionRef.current.x,
                dragPositionRef.current.y,
            ])
            onDragEnd?.({ lat: nextLat, lng: nextLng })
        } finally {
            releasePointer(event)
        }
    }

    // draggable can flip mid-drag; without these the ref stays true forever and
    // applyPosition stops tracking the geography on every later viewport change.
    const handlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isDraggingRef.current) return
        releasePointer(event)
    }

    return (
        <div
            {...rest}
            ref={containerRef}
            className={classNames("absolute select-none", draggable && "cursor-grab", className)}
            onWheel={scrollThroughToMap ? forwardWheelToMap : rest.onWheel}
            onMouseMove={(event) => {
                forwardMouseMoveToMap(event)
                rest.onMouseMove?.(event)
            }}
            onDragStart={(event) => event.preventDefault()}
            onPointerDown={draggable ? handlePointerDown : rest.onPointerDown}
            onPointerMove={draggable ? handlePointerMove : rest.onPointerMove}
            onPointerUp={draggable ? handlePointerUp : rest.onPointerUp}
            onPointerCancel={handlePointerCancel}
            onLostPointerCapture={handlePointerCancel}
        >
            {children}
        </div>
    )
}
